/* eslint-disable import/max-dependencies */
import React, { useEffect } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import type { Dispatch, Action } from "redux";
import { useParams, useNavigate } from "react-router";
import {
  Container,
  Box,
  Grid,
  CircularProgress,
} from "@mui/material";
import { LoadingButton } from "@fas/ui-core";
import { setErrors, getErrors } from "@fas/ui-framework";
import type { ErrorsMapper } from "@fas/ui-framework";
import { getRedirectId, getRedirectInfo } from "../../selectors/redirect";
import {
  getIsRedirectLoading,
  getIsSaveRedirectLoading,
} from "../../selectors/loading";
import RedirectlInfo from "../../components/RedirectInfo";
import {
  getRedirectSaga,
  saveRedirect,
} from "../../actions/redirect";
import {
  changeRedirectPageUrl,
  changeRedirectStatusCode,
  changeRedirectRedirectUrl,
} from "../../actions/redirectInfo";
import { redirectBackupStore } from "../../pages/AppStoreWrapper/store";
import { setBackup, restoreBackup } from "../../actions/backup";
import type { State } from "../../pages/AppStoreWrapper";
import type { State as RedirectInfoState } from "../../reducers/redirectInfo";
import type { StateToProps, DispatchToProps, Props, RedirectInfo as RedirectInfoType } from "./types/RedirectInfo.type";

const mapStateToProps: (state: State) => StateToProps = (state) => ({
  isRedirectLoading: getIsRedirectLoading(state),
  isSaveRedirectLoading: getIsSaveRedirectLoading(state),
  redirectId: getRedirectId(state),
  redirectInfo: getRedirectInfo(state),
  errors: getErrors(state),
});

const mapDispatchToProps: (arg0: Dispatch<any>) => DispatchToProps = dispatch => ({
  ...bindActionCreators({
    onChangePageUrl: changeRedirectPageUrl,
    onChangeStatusCode: changeRedirectStatusCode,
    onChangeRedirectUrl: changeRedirectRedirectUrl,
    onGetRedirect: getRedirectSaga,
    onSetErrors: setErrors,
    onSaveRedirect: saveRedirect,
    onSetBackup: setBackup,
    onRestoreBackup: restoreBackup,
  }, dispatch),
});

function RedirectInfo({
  isRedirectLoading,
  isSaveRedirectLoading,
  onSaveRedirect,
  redirectInfo,
  errors,
  onChangePageUrl,
  onChangeStatusCode,
  onChangeRedirectUrl,
  onGetRedirect,
  onSetErrors,
  onSetBackup,
  onRestoreBackup,
}: Props) {
  const {
    pageUrl,
    statusCode,
    redirectUrl,
  }: RedirectInfoState = redirectInfo;
  const statusCodesList: string[] = ["301", "302"];
  const { id: redirectId } = useParams();
  const disableSave: boolean = isRedirectLoading || !(!errors.size);

  const navigate = useNavigate();

  const redirect: RedirectInfoType = {
    pageUrl,
    statusCode: Number(statusCode),
    redirectUrl,
  };

  useEffect(() => {
    onSetBackup(redirectBackupStore);
    if (redirectId) onGetRedirect(redirectId);
    return () => {
      onRestoreBackup();
    };
  }, [redirectId, onGetRedirect]);

  const handleChangePageUrl: (value: string) => void = (value: string) => {
    errors.has("pageUrl") && onSetErrors({});
    onChangePageUrl(value);
  };
  const handleChangeStatusCode: (value: string) => void = (value: string) => {
    errors.has("statusCode") && onSetErrors({});
    onChangeStatusCode(value);
  };
  const handleChangeRedirectUrl: (value: string) => void = (value: string) => {
    errors.has("redirectUrl") && onSetErrors({});
    onChangeRedirectUrl(value);
  };

  return (
    <Container maxWidth="md">
      <Box data-testid="pages-general-info">
        {isRedirectLoading ? (
          <Grid container justifyContent="center">
            <Box mt={40}>
              <CircularProgress />
            </Box>
          </Grid>
        ) : (
          <RedirectlInfo
            pageUrl={pageUrl}
            statusCode={statusCode}
            statusCodesList={statusCodesList}
            redirectUrl={redirectUrl}
            errors={errors.toJS() as ErrorsMapper}
            onChangePageUrl={handleChangePageUrl}
            onChangeStatusCode={handleChangeStatusCode}
            onChangeRedirectUrl={handleChangeRedirectUrl}
          />
        )}
      </Box>
      <Box mt={3} display="flex" justifyContent="flex-end">
        <LoadingButton
          type="button"
          size="medium"
          variant="contained"
          color="primary"
          onClick={() => onSaveRedirect(redirectId, redirect, navigate)}
          data-testid="next-button"
          loading={isSaveRedirectLoading}
          disabled={disableSave}
        >
          Save
        </LoadingButton>
      </Box>
    </Container>
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(RedirectInfo);

